import { useAppSelector } from 'hooks/use-app-selector'
import { useEffect, useState } from 'react'
import { selectAiSubscriptionLoading, selectIsAiSubscriptionActive } from 'store/slices/subscription/subscription'

import { UpsellPrompt } from '../components/upsell-prompts'

// Storage key for the popup's last closed timestamp
const POPUP_LAST_CLOSED_KEY = 'ad_blocker_upsell_popup_last_closed'
// How long to wait before showing popup again (2 hours in milliseconds)
const POPUP_HIDE_DURATION = 2 * 60 * 60 * 1000

// Ad-blocker detection upsell prompt - automatically shows popup
export const AdBlockerUpsell: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false)
  const isAiSubscriptionLoading = useAppSelector(selectAiSubscriptionLoading)
  const isSubscriber = useAppSelector(selectIsAiSubscriptionActive)

  // Check if popup should be hidden based on last close time
  const shouldShowPopup = () => {
    if (typeof window === 'undefined') return false // Server-side rendering check

    const lastClosedTime = localStorage.getItem(POPUP_LAST_CLOSED_KEY)
    if (!lastClosedTime) return true

    const now = new Date().getTime()
    const timeSinceClosed = now - parseInt(lastClosedTime, 10)

    return timeSinceClosed > POPUP_HIDE_DURATION
  }

  useEffect(() => {
    // Wait for subscription loading to complete before making decisions
    if (isAiSubscriptionLoading) return

    if (!isSubscriber && shouldShowPopup()) {
      setIsVisible(true)
    }
  }, [isSubscriber, isAiSubscriptionLoading])

  // Hide popup if user becomes a subscriber
  useEffect(() => {
    if (isSubscriber && isVisible) {
      setIsVisible(false)
    }
  }, [isSubscriber, isVisible])

  // Don't show popup if user is already a subscriber
  useEffect(() => {
    // Wait for subscription loading to complete before making decisions
    if (isAiSubscriptionLoading) return

    if (isSubscriber) {
      setIsVisible(false)
    }
  }, [isSubscriber, isAiSubscriptionLoading])

  const handleAllowMe2Hours = () => {
    setIsVisible(false)

    // Store the current timestamp when "Allow me 2 hours" is clicked
    if (typeof window !== 'undefined') {
      localStorage.setItem(POPUP_LAST_CLOSED_KEY, new Date().getTime().toString())
    }
  }

  // Don't render if popup shouldn't be visible or if still loading
  if (!isVisible || isAiSubscriptionLoading) return null

  return (
    <UpsellPrompt
      title="Go Ad-Free Legally"
      description="Support our platform legally by upgrading to our ad-free plan. Remove all advertisements and enjoy a distraction-free typing experience."
      showOnlyAdFree={true}
      showAllowMe2Hours={true}
      onAllowMe2Hours={handleAllowMe2Hours}
    />
  )
}
